import {
  Certificate,
  CertificateChange,
  LoadBalancer,
  LoadBalancerChange,
  type CreateCertificateRequest,
  type CreateLoadBalancerRequest,
  type LoadBalancerAlgorithm,
  type LoadBalancerServiceInput,
  type LoadBalancerTargetInput,
  type RemoveLoadBalancerTargetRequest,
  type ReverseDnsRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

const base = (projectId: string) => `/projects/${projectId}/load-balancers`;

/** The project's load balancers; live updates refresh them through the `projects` key. */
export const useLoadBalancers = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "load-balancers"],
    queryFn: () => api(base(projectId), { schema: z.array(LoadBalancer) }),
  });

export const useLoadBalancer = (projectId: string, id: string) =>
  useQuery({
    queryKey: ["projects", projectId, "load-balancers", id],
    queryFn: () => api(`${base(projectId)}/${id}`, { schema: LoadBalancer }),
  });

/** Writes also change what server, network and certificate pages show: every project query is refreshed. */
function useProjectWrite<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export const useCreateLoadBalancer = (projectId: string) =>
  useProjectWrite((body: CreateLoadBalancerRequest) =>
    api(base(projectId), {
      method: "POST",
      body,
      schema: LoadBalancerChange,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateLoadBalancer = (projectId: string, id: string) =>
  useProjectWrite((body: { name?: string; labels?: Record<string, string> }) =>
    api(`${base(projectId)}/${id}`, { method: "PATCH", body, schema: LoadBalancer }),
  );

export const useAddService = (projectId: string, id: string) =>
  useProjectWrite((body: LoadBalancerServiceInput) =>
    api(`${base(projectId)}/${id}/services`, {
      method: "POST",
      body,
      schema: LoadBalancerChange,
    }),
  );

export const useUpdateService = (projectId: string, id: string) =>
  useProjectWrite((body: LoadBalancerServiceInput) =>
    api(`${base(projectId)}/${id}/services/${body.listenPort}`, {
      method: "PUT",
      body,
      schema: LoadBalancerChange,
    }),
  );

export const useDeleteService = (projectId: string, id: string) =>
  useProjectWrite((port: number) =>
    api(`${base(projectId)}/${id}/services/${port}`, {
      method: "DELETE",
      schema: LoadBalancerChange,
    }),
  );

export const useAddTarget = (projectId: string, id: string) =>
  useProjectWrite((body: LoadBalancerTargetInput) =>
    api(`${base(projectId)}/${id}/targets`, { method: "POST", body, schema: LoadBalancerChange }),
  );

export const useRemoveTarget = (projectId: string, id: string) =>
  useProjectWrite((body: RemoveLoadBalancerTargetRequest) =>
    api(`${base(projectId)}/${id}/targets/remove`, {
      method: "POST",
      body,
      schema: LoadBalancerChange,
    }),
  );

const useAction = <B>(projectId: string, id: string, name: string) =>
  useProjectWrite((body: B) =>
    api(`${base(projectId)}/${id}/actions/${name}`, {
      method: "POST",
      body,
      schema: LoadBalancerChange,
    }),
  );

export const useChangeLoadBalancerType = (projectId: string, id: string) =>
  useAction<{ type: string }>(projectId, id, "change-type");
export const useChangeAlgorithm = (projectId: string, id: string) =>
  useAction<{ algorithm: LoadBalancerAlgorithm }>(projectId, id, "change-algorithm");
export const useAttachLoadBalancerNetwork = (projectId: string, id: string) =>
  useAction<{ networkId: string; ip?: string }>(projectId, id, "attach-network");
export const useDetachLoadBalancerNetwork = (projectId: string, id: string) =>
  useAction<{ networkId: string }>(projectId, id, "detach-network");
export const usePublicInterface = (projectId: string, id: string) =>
  useAction<{ enabled: boolean }>(projectId, id, "public-interface");
export const useLoadBalancerProtection = (projectId: string, id: string) =>
  useAction<{ delete: boolean }>(projectId, id, "protection");

/** Reverse DNS of a balancer's own addresses, in the shape the shared dialog takes. */
export function useLoadBalancerReverseDns(projectId: string) {
  return useProjectWrite(({ id, body }: { id: string; body: ReverseDnsRequest }) =>
    api(`${base(projectId)}/${id}/actions/reverse-dns`, {
      method: "POST",
      body,
      schema: LoadBalancerChange,
    }),
  );
}

export const useDeleteLoadBalancer = (projectId: string) =>
  useProjectWrite((id: string) => api(`${base(projectId)}/${id}`, { method: "DELETE" }));

// --- Certificates ----------------------------------------------------------------

const certificates = (projectId: string) => `/projects/${projectId}/certificates`;

export const useCertificates = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "certificates"],
    queryFn: () => api(certificates(projectId), { schema: z.array(Certificate) }),
  });

export const useCreateCertificate = (projectId: string) =>
  useProjectWrite((body: CreateCertificateRequest) =>
    api(certificates(projectId), {
      method: "POST",
      body,
      schema: CertificateChange,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateCertificate = (projectId: string) =>
  useProjectWrite(
    ({ id, ...body }: { id: string; name?: string; labels?: Record<string, string> }) =>
      api(`${certificates(projectId)}/${id}`, { method: "PATCH", body, schema: Certificate }),
  );

export const useRetryCertificate = (projectId: string) =>
  useProjectWrite((id: string) =>
    api(`${certificates(projectId)}/${id}/actions/retry`, {
      method: "POST",
      schema: CertificateChange,
    }),
  );

export const useDeleteCertificate = (projectId: string) =>
  useProjectWrite((id: string) => api(`${certificates(projectId)}/${id}`, { method: "DELETE" }));
